import { memo, useMemo, useState } from 'react';
import { Tree, Tag, Tooltip, Col, Row, Typography } from 'antd';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { PERIOD1_COLOR, PERIOD2_COLOR } from './constants/comparisonConstants';
import { calculatePercentages as calculatePercentagesUtil, isValidArray } from './utils/comparisonUtils';
import { tooltipStyle, tooltipTitleStyle, tooltipValueStyle } from './styles/comparisonStyles';

function LocationTreeComparison({ tasksByLocation = [], tasksByLocation2 = [], handleAnswersSearchLocation, renderPeriodWithDot, filterAnswersRequest }) {
    const { t } = useTranslation();
    // Estados de seleção e hover para o gráfico
    const [activeSlices, setActiveSlices] = useState([]);
    const [hoveredSlice, setHoveredSlice] = useState(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    // Validação de props
    if (!isValidArray(tasksByLocation) && !isValidArray(tasksByLocation2)) {
        return null;
    }

    // Combina dados de ambos os períodos mantendo separados
    const combinedData = useMemo(() => {
        const locationMap = new Map();
        
        // Adiciona dados do período 1
        tasksByLocation.forEach(item => {
            if (item.totalTasks > 0) {
                locationMap.set(item.locationId, {
                    locationId: item.locationId,
                    locationName: item.locationName,
                    totalTasksPeriod1: item.totalTasks,
                    totalTasksPeriod2: 0
                });
            }
        });
        
        // Adiciona dados do período 2
        tasksByLocation2.forEach(item => {
            if (item.totalTasks > 0) {
                const existing = locationMap.get(item.locationId);
                if (existing) {
                    existing.totalTasksPeriod2 = item.totalTasks;
                } else {
                    locationMap.set(item.locationId, {
                        locationId: item.locationId,
                        locationName: item.locationName,
                        totalTasksPeriod1: 0,
                        totalTasksPeriod2: item.totalTasks
                    });
                }
            }
        });
        
        return Array.from(locationMap.values());
    }, [tasksByLocation, tasksByLocation2]);

    // Mapeia caminho -> ID original
    const idMap = useMemo(
        () => {
            const map = {};
            combinedData.forEach(({ locationName, locationId }) => {
                map[locationName] = locationId;
            });
            return map;
        },
        [combinedData]
    );

    // Soma recursiva: próprio + filhos (considerando ambos os períodos)
    const aggregatedCounts = useMemo(() => {
        const map = {};
        combinedData.forEach(({ locationName, totalTasksPeriod1, totalTasksPeriod2 }) => {
            const totalTasks = totalTasksPeriod1 + totalTasksPeriod2;
            let prefix = '';
            locationName.split(' > ').forEach(part => {
                prefix = prefix ? `${prefix} > ${part}` : part;
                if (!map[prefix]) {
                    map[prefix] = { period1: 0, period2: 0 };
                }
                map[prefix].period1 += totalTasksPeriod1;
                map[prefix].period2 += totalTasksPeriod2;
            });
        });
        return map;
    }, [combinedData]);

    // Tarefas apenas no nível exato
    const ownCounts = useMemo(
        () => {
            const map = {};
            combinedData.forEach(({ locationName, totalTasksPeriod1, totalTasksPeriod2 }) => {
                if (!map[locationName]) {
                    map[locationName] = { period1: 0, period2: 0 };
                }
                map[locationName].period1 += totalTasksPeriod1;
                map[locationName].period2 += totalTasksPeriod2;
            });
            return map;
        },
        [combinedData]
    );

    // Monta árvore com tags, tooltips, desabilitação de nós e estilo compacto
    const treeData = useMemo(() => {
        const nodeMap = Object.entries(aggregatedCounts).reduce((acc, [fullKey, totals]) => {
            const label = fullKey.split(' > ').pop();
            const own = ownCounts[fullKey] || { period1: 0, period2: 0 };
            const hasId = idMap[fullKey] != null;
            acc[fullKey] = {
                key: fullKey,
                title: (
                    <span style={{ fontSize: 12, lineHeight: '15px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tooltip title="Tarefas somente neste nível">
                            <span style={{ display: 'flex', flexDirection: 'row', gap: 12, marginRight: 4 }}>
                                {/* Coluna Período 1 */}
                                <span style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'flex-start', 
                                    minWidth: '50px',
                                    borderRight: '1px solid #e8e8e8',
                                    paddingRight: 8
                                }}>
                                    {own.period1 > 0 ? (
                                        <Tag color={PERIOD1_COLOR} style={{ padding: "4px 6px", lineHeight: "10px", margin: 0 }}>
                                            {own.period1}
                                        </Tag>
                                    ) : (
                                        <span style={{ height: '22px' }}></span>
                                    )}
                                </span>
                                {/* Coluna Período 2 */}
                                <span style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'flex-start', 
                                    minWidth: '50px'
                                }}>
                                    {own.period2 > 0 ? (
                                        <Tag color={PERIOD2_COLOR} style={{ padding: "4px 6px", lineHeight: "10px", margin: 0 }}>
                                            {own.period2}
                                        </Tag>
                                    ) : (
                                        <span style={{ height: '22px' }}></span>
                                    )}
                                </span>
                            </span>
                        </Tooltip>
                        <strong>{label}</strong>
                    </span>
                ),
                children: [],
                disabled: !hasId,
                style: { padding: '0px' }
            };
            return acc;
        }, {});

        Object.keys(nodeMap).forEach(fullKey => {
            const idx = fullKey.lastIndexOf(' > ');
            if (idx !== -1) {
                const parentKey = fullKey.slice(0, idx);
                nodeMap[parentKey].children.push(nodeMap[fullKey]);
            }
        });

        return Object.values(nodeMap).filter(node => !node.key.includes(' > '));
    }, [aggregatedCounts, ownCounts, idMap]);

    // Monta árvore apenas para Período 1
    const treeDataPeriod1 = useMemo(() => {
        const nodeMap = Object.entries(aggregatedCounts).reduce((acc, [fullKey, totals]) => {
            const own = ownCounts[fullKey] || { period1: 0, period2: 0 };
            // Filtra apenas nós que têm dados no período 1
            if (totals.period1 === 0 && own.period1 === 0) {
                return acc;
            }
            const label = fullKey.split(' > ').pop();
            const hasId = idMap[fullKey] != null;
            acc[fullKey] = {
                key: fullKey,
                title: (
                    <span style={{ fontSize: 12, lineHeight: '15px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {own.period1 > 0 && (
                            <Tooltip title="Tarefas somente neste nível">
                                <Tag color={PERIOD1_COLOR} style={{ padding: "4px 6px", lineHeight: "10px", margin: 0 }}>
                                    {own.period1}
                                </Tag>
                            </Tooltip>
                        )}
                    <strong>{label} {totals.period1 != own.period1 ? <>- (Total: {totals.period1})</> : ""}</strong>
                    </span>
                ),
                children: [],
                disabled: !hasId,
                style: { padding: '0px' }
            };
            return acc;
        }, {});

        Object.keys(nodeMap).forEach(fullKey => {
            const idx = fullKey.lastIndexOf(' > ');
            if (idx !== -1) {
                const parentKey = fullKey.slice(0, idx);
                if (nodeMap[parentKey]) {
                    nodeMap[parentKey].children.push(nodeMap[fullKey]);
                }
            }
        });

        return Object.values(nodeMap).filter(node => !node.key.includes(' > '));
    }, [aggregatedCounts, ownCounts, idMap]);

    // Monta árvore apenas para Período 2
    const treeDataPeriod2 = useMemo(() => {
        const nodeMap = Object.entries(aggregatedCounts).reduce((acc, [fullKey, totals]) => {
            const own = ownCounts[fullKey] || { period1: 0, period2: 0 };
            // Filtra apenas nós que têm dados no período 2
            if (totals.period2 === 0 && own.period2 === 0) {
                return acc;
            }
            const label = fullKey.split(' > ').pop();
            const hasId = idMap[fullKey] != null;
            acc[fullKey] = {
                key: fullKey,
                title: (
                    <span style={{ fontSize: 12, lineHeight: '15px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {own.period2 > 0 && (
                            <Tooltip title="Tarefas somente neste nível">
                                <Tag color={PERIOD2_COLOR} style={{ padding: "4px 6px", lineHeight: "10px", margin: 0 }}>
                                    {own.period2}
                                </Tag>
                            </Tooltip>
                        )}
                    <strong>{label} {totals.period2 != own.period2 ? <>- (Total: {totals.period2})</> : ""}</strong>
                    </span>
                ),
                children: [],
                disabled: !hasId,
                style: { padding: '0px' }
            };
            return acc;
        }, {});

        Object.keys(nodeMap).forEach(fullKey => {
            const idx = fullKey.lastIndexOf(' > ');
            if (idx !== -1) {
                const parentKey = fullKey.slice(0, idx);
                if (nodeMap[parentKey]) {
                    nodeMap[parentKey].children.push(nodeMap[fullKey]);
                }
            }
        });

        return Object.values(nodeMap).filter(node => !node.key.includes(' > '));
    }, [aggregatedCounts, ownCounts, idMap]);

    // Prepara dados do PieChart para Período 1
    const pieDataPeriod1 = useMemo(() => {
        const data = [];
        combinedData.forEach(({ locationName, locationId, totalTasksPeriod1 }) => {
            const shortName = locationName.split(' > ').pop();
            if (totalTasksPeriod1 > 0) {
                data.push({
                    name: shortName,
                    fullName: locationName,
                    value: totalTasksPeriod1,
                    id: locationId,
                    period: 1,
                    locationShortName: shortName
                });
            }
        });
        return data;
    }, [combinedData]);

    // Prepara dados do PieChart para Período 2
    const pieDataPeriod2 = useMemo(() => {
        const data = [];
        combinedData.forEach(({ locationName, locationId, totalTasksPeriod2 }) => {
            const shortName = locationName.split(' > ').pop();
            if (totalTasksPeriod2 > 0) {
                data.push({
                    name: shortName,
                    fullName: locationName,
                    value: totalTasksPeriod2,
                    id: locationId,
                    period: 2,
                    locationShortName: shortName
                });
            }
        });
        return data;
    }, [combinedData]);

    // Calcula o total para porcentagem do Período 1
    const totalTasksPeriod1 = useMemo(() => {
        return pieDataPeriod1.reduce((sum, { value }) => sum + value, 0);
    }, [pieDataPeriod1]);

    // Calcula o total para porcentagem do Período 2
    const totalTasksPeriod2 = useMemo(() => {
        return pieDataPeriod2.reduce((sum, { value }) => sum + value, 0);
    }, [pieDataPeriod2]);

    // Função para calcular porcentagem do Período 1 com arredondamento que soma exatamente 100%
    const calculatePercentagesPeriod1 = useMemo(() => {
        if (totalTasksPeriod1 === 0 || pieDataPeriod1.length === 0) return new Map();
        
        const values = pieDataPeriod1.map(item => item.value);
        return calculatePercentagesUtil(values, totalTasksPeriod1);
    }, [pieDataPeriod1, totalTasksPeriod1]);

    // Função para calcular porcentagem do Período 2 com arredondamento que soma exatamente 100%
    const calculatePercentagesPeriod2 = useMemo(() => {
        if (totalTasksPeriod2 === 0 || pieDataPeriod2.length === 0) return new Map();
        
        const values = pieDataPeriod2.map(item => item.value);
        return calculatePercentagesUtil(values, totalTasksPeriod2);
    }, [pieDataPeriod2, totalTasksPeriod2]);

    // Alterna seleção
    const toggleSelection = (id) => {
        setActiveSlices(prev => {
            const isActive = prev.includes(id);
            const newArr = isActive ? prev.filter(i => i !== id) : [...prev, id];
            handleAnswersSearchLocation(id, 'combo', true);
            return newArr;
        });
    };

    // Handlers de clique/hover no Pie - Período 1
    const onPieClickPeriod1 = (_, index) => pieDataPeriod1[index] && toggleSelection(pieDataPeriod1[index].id);
    const onPieMouseEnterPeriod1 = (data, index, e) => {
        if (data && data.payload) {
            const id = data.payload.id;
            if (id !== hoveredSlice) {
                setHoveredSlice(id);
            }
        }
    };

    // Handlers de clique/hover no Pie - Período 2
    const onPieClickPeriod2 = (_, index) => pieDataPeriod2[index] && toggleSelection(pieDataPeriod2[index].id);
    const onPieMouseEnterPeriod2 = (data, index, e) => {
        if (data && data.payload) {
            const id = data.payload.id;
            if (id !== hoveredSlice) {
                setHoveredSlice(id);
            }
        }
    };

    const onPieMouseLeave = () => setHoveredSlice(null);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            {/* Container dos dois gráficos de donut */}
            <div style={{ width: '50%', display: 'flex', gap: 8 }}>
                {/* Gráfico Período 1 */}
                <div style={{ width: '50%', height: (pieDataPeriod1.length > 0 || pieDataPeriod2.length > 0) ? 250 : 0, maxHeight: 250 }}>
                    {renderPeriodWithDot && filterAnswersRequest && (
                        <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                            {renderPeriodWithDot(1, filterAnswersRequest?.dateRange)}
                        </Typography.Text>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={pieDataPeriod1}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={55}
                                outerRadius={85}
                                minAngle={5}
                                paddingAngle={2}
                                isAnimationActive={!hasAnimated}
                                animationBegin={0}
                                animationDuration={800}
                                animationEasing="ease-out"
                                onAnimationEnd={() => setHasAnimated(true)}
                                onClick={onPieClickPeriod1}
                                onMouseEnter={onPieMouseEnterPeriod1}
                                onMouseLeave={onPieMouseLeave}
                                cursor="pointer"
                                disabled={false}
                                label={({ payload, cx, cy, midAngle, outerRadius, index }) => {
                                    if (!payload) return null;
                                    const { value, locationShortName } = payload;
                                    // Usa porcentagem ajustada que garante soma = 100%
                                    const percent = calculatePercentagesPeriod1.get(index) ?? (totalTasksPeriod1 > 0 ? (value / totalTasksPeriod1) * 100 : 0);
                                    const RADIAN = Math.PI / 180;
                                    const radius = outerRadius + 30;
                                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                    return (
                                        <text
                                            x={x}
                                            y={y}
                                            fill="#262626"
                                            textAnchor={x > cx ? "start" : "end"}
                                            dominantBaseline="central"
                                            fontSize="11px"
                                            fontWeight="600"
                                        >
                                            <tspan>{value} ({percent.toFixed(2)}%)</tspan>
                                        </text>
                                    );
                                }}
                            >
                                {pieDataPeriod1.map((entry, idx) => {
                                    const opacity = hoveredSlice != null
                                        ? (entry.id === hoveredSlice ? 1 : 0.3)
                                        : (activeSlices.length === 0 || activeSlices.includes(entry.id) ? 1 : 0.3);
                                    const isHovered = hoveredSlice === entry.id;
                                    const isActive = activeSlices.length === 0 || activeSlices.includes(entry.id);
                                    return (
                                        <Cell
                                            key={`p1-${entry.id}-${idx}`}
                                            fill={PERIOD1_COLOR}
                                            opacity={opacity}
                                            stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isHovered || isActive ? 3 : 0}
                                        />
                                    );
                                })}
                            </Pie>
                            <RechartsTooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const { value, locationShortName } = payload[0].payload;
                                        // Encontra o índice do item no pieDataPeriod1
                                        const itemIndex = pieDataPeriod1.findIndex(item => item.value === value && item.locationShortName === locationShortName);
                                        // Usa porcentagem ajustada que garante soma = 100%
                                        const percent = itemIndex >= 0 && calculatePercentagesPeriod1.has(itemIndex) 
                                            ? calculatePercentagesPeriod1.get(itemIndex) 
                                            : (totalTasksPeriod1 > 0 ? (value / totalTasksPeriod1) * 100 : 0);
                                        return (
                                            <div style={tooltipStyle}>
                                                <p style={tooltipTitleStyle}>
                                                    {locationShortName} - Período 1
                                                </p>
                                                <p style={tooltipValueStyle}>{value}</p>
                                                <p style={{ margin: 0, color: '#595959' }}>{t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{percent.toFixed(2)}%</strong></p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }} 
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Gráfico Período 2 */}
                <div style={{ width: '50%', height: (pieDataPeriod1.length > 0 || pieDataPeriod2.length > 0) ? 250 : 0, maxHeight: 250 }}>
                    {renderPeriodWithDot && filterAnswersRequest && (
                        <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: '15%', marginRight: 0, display: 'block' }}>
                            {renderPeriodWithDot(2, filterAnswersRequest?.dateRange2)}
                        </Typography.Text>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={pieDataPeriod2}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={55}
                                outerRadius={85}
                                minAngle={5}
                                paddingAngle={2}
                                isAnimationActive={!hasAnimated}
                                animationBegin={0}
                                animationDuration={800}
                                animationEasing="ease-out"
                                onAnimationEnd={() => setHasAnimated(true)}
                                onClick={onPieClickPeriod2}
                                onMouseEnter={onPieMouseEnterPeriod2}
                                onMouseLeave={onPieMouseLeave}
                                cursor="pointer"
                                disabled={false}
                                label={({ payload, cx, cy, midAngle, outerRadius, index }) => {
                                    if (!payload) return null;
                                    const { value, locationShortName } = payload;
                                    // Usa porcentagem ajustada que garante soma = 100%
                                    const percent = calculatePercentagesPeriod2.get(index) ?? (totalTasksPeriod2 > 0 ? (value / totalTasksPeriod2) * 100 : 0);
                                    const RADIAN = Math.PI / 180;
                                    const radius = outerRadius + 30;
                                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                    return (
                                        <text
                                            x={x}
                                            y={y}
                                            fill="#262626"
                                            textAnchor={x > cx ? "start" : "end"}
                                            dominantBaseline="central"
                                            fontSize="11px"
                                            fontWeight="600"
                                        >
                                            <tspan>{value} ({percent.toFixed(2)}%)</tspan>
                                        </text>
                                    );
                                }}
                            >
                                {pieDataPeriod2.map((entry, idx) => {
                                    const opacity = hoveredSlice != null
                                        ? (entry.id === hoveredSlice ? 1 : 0.3)
                                        : (activeSlices.length === 0 || activeSlices.includes(entry.id) ? 1 : 0.3);
                                    const isHovered = hoveredSlice === entry.id;
                                    const isActive = activeSlices.length === 0 || activeSlices.includes(entry.id);
                                    return (
                                        <Cell
                                            key={`p2-${entry.id}-${idx}`}
                                            fill={PERIOD2_COLOR}
                                            opacity={opacity}
                                            stroke={isHovered || isActive ? '#fff' : 'transparent'}
                                            strokeWidth={isHovered || isActive ? 3 : 0}
                                        />
                                    );
                                })}
                            </Pie>
                            <RechartsTooltip 
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const { value, locationShortName } = payload[0].payload;
                                        // Encontra o índice do item no pieDataPeriod2
                                        const itemIndex = pieDataPeriod2.findIndex(item => item.value === value && item.locationShortName === locationShortName);
                                        // Usa porcentagem ajustada que garante soma = 100%
                                        const percent = itemIndex >= 0 && calculatePercentagesPeriod2.has(itemIndex) 
                                            ? calculatePercentagesPeriod2.get(itemIndex) 
                                            : (totalTasksPeriod2 > 0 ? (value / totalTasksPeriod2) * 100 : 0);
                                        return (
                                            <div style={tooltipStyle}>
                                                <p style={tooltipTitleStyle}>
                                                    {locationShortName} - Período 2
                                                </p>
                                                <p style={tooltipValueStyle}>{value}</p>
                                                <p style={{ margin: 0, color: '#595959' }}>{t('Dashboard.Tooltip.Percentage')}: <strong style={{ color: '#4085c6' }}>{percent.toFixed(2)}%</strong></p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }} 
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Árvore compacta P1*/}
            <Col span={12}>
                <Row gutter={[16, 0]}>
                    {/* Árvore compacta P1 */}
                    <Col span={12}>
                        
                        <div className="ant-loc" style={{ maxHeight: 250, overflowY: 'auto' }}>
                            <Tree
                                treeData={treeDataPeriod1}
                                indent={8}
                                virtual
                                height={250}
                                itemHeight={20}
                                defaultExpandAll
                                showIcon={false}
                                showLine={false}
                                onSelect={([key]) => {
                                    const id = idMap[key];
                                    if (id != null) handleAnswersSearchLocation(id, 'combo', true);
                                }}
                            />
                        </div>
                    </Col>
                    {/* Árvore compacta P2 */}
                    <Col span={12}>
                        
                        <div className="ant-loc" style={{ maxHeight: 250, overflowY: 'auto' }}>
                            <Tree
                                treeData={treeDataPeriod2}
                                indent={8}
                                virtual
                                height={250}
                                itemHeight={20}
                                defaultExpandAll
                                showIcon={false}
                                showLine={false}
                                onSelect={([key]) => {
                                    const id = idMap[key];
                                    if (id != null) handleAnswersSearchLocation(id, 'combo', true);
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </Col>
        </div >
    );
}

export default memo(LocationTreeComparison);
